import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import moment from "moment";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { alpha } from "@mui/material/styles";
import KeyboardDoubleArrowDownRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowDownRounded";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import { BondOrderHistoryResponse } from "./helpers";
import {
  getExternalTagLabel,
  getOrderInvestmentAmount,
  getOrderSource,
  isInternalOrderDeleted,
  usesExternalDeleteApi,
} from "./helpers";
import {
  parseDate,
  replaceCharsForSecurityName,
  toINR,
} from "../../../Helpers/functions";
import copy from "copy-to-clipboard";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

const IconCell = (props: TableCellProps) => (
  <TableCell
    padding="none"
    sx={{
      width: "1.5rem",
      maxWidth: "1.5rem",
      px: 0.5,
      py: 0.25,
      textAlign: "center",
      ...props.sx,
    }}
    {...props}
  />
);

interface MobileViewProps {
  order: BondOrderHistoryResponse;
  aggregatedCashflows: Record<string, any[]>;
  openMain: Record<string, boolean>;
  openYear: Record<string, Record<string, boolean>>;
  setOpenMain: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setOpenYear: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, boolean>>>
  >;
  onDeleteOrder?: (order: BondOrderHistoryResponse) => void;
  deletingOrderUuid?: string | null;
}

const MobileView: React.FC<MobileViewProps> = ({
  order,
  aggregatedCashflows,
  openMain,
  openYear,
  setOpenMain,
  setOpenYear,
  onDeleteOrder,
  deletingOrderUuid,
}) => {
  const toggleAllYears = () => {
    const years = aggregatedCashflows[order.uuid]?.map((y) => y.year) || [];
    const allExpanded = years.every((year) => openYear[order.uuid]?.[year]);

    setOpenYear((prev) => {
      const newState = { ...prev };
      if (!newState[order.uuid]) newState[order.uuid] = {};

      years.forEach((year) => {
        newState[order.uuid][year] = !allExpanded;
      });

      return newState;
    });
  };

  const areAllYearsExpanded = useMemo(() => {
    const years = aggregatedCashflows[order.uuid]?.map((y) => y.year) || [];
    return (
      years.length > 0 && years.every((year) => openYear[order.uuid]?.[year])
    );
  }, [aggregatedCashflows, openYear, order.uuid]);

  const lastCallMoment = parseDate(order.last_call_date);
  const maturityMoment = parseDate(order.last_maturity_date);
  const firstPutOptionDate = order.put_options?.[0]?.date;
  const putMoment = parseDate(firstPutOptionDate);
  const hasMatchingPut =
    !!putMoment && !!maturityMoment && putMoment.isSame(maturityMoment, "day");
  const displayMoment =
    lastCallMoment || (hasMatchingPut ? putMoment : maturityMoment);
  const displayLabel = lastCallMoment
    ? "Call Date"
    : hasMatchingPut
    ? "Put Date"
    : "Maturity Date";
  const externalTagLabel = getExternalTagLabel(order);
  const isIpoTag = externalTagLabel === "IPO";
  const orderSource = getOrderSource(order);
  const isDeletedOrder = isInternalOrderDeleted(order);
  const deleteLabel = usesExternalDeleteApi(order)
    ? "Remove External Bond"
    : "Remove Internal Bond";
  const canDelete = Boolean(order.uuid) && !isDeletedOrder;
  const orderCashflows = aggregatedCashflows[order.uuid] || [];
  const principalPayout = orderCashflows.reduce(
    (total, year) => total + Number(year.maturity || 0),
    0
  );
  const interestPayout = orderCashflows.reduce(
    (total, year) => total + Number(year.interest || 0),
    0
  );

  const summaryItems = useMemo(() => {
    const items = [
      {
        label: "Security Type",
        value: order.security_type,
      },
      {
        label: "Rating",
        value: order.rating,
      },
      {
        label: "Coupon",
        value: order.security_name.split(" ")[0],
      },
      {
        label: "Yield",
        value: `${order.sell_yield}%`,
      },
      {
        label: "Interest Frequency",
        value: order.frequency_of_interest,
      },
      {
        label: "Qty",
        value: order.no_of_bonds,
      },
      {
        label: "Investment Amount",
        value: toINR(getOrderInvestmentAmount(order), 2),
      },
    ];

    if (externalTagLabel === "EXTERNAL" && orderSource) {
      items.push({
        label: "Source",
        value: orderSource,
      });
    }

    return items;
  }, [externalTagLabel, order, orderSource]);

  return (
    <Box
      sx={{
        mb: 2,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: 14,
                fontWeight: 800,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minWidth: 0,
              }}
            >
              {order.security_name}
            </Typography>
            {(externalTagLabel || isDeletedOrder) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  mt: 0.5,
                }}
              >
                {externalTagLabel && (
                  <Chip
                    label={externalTagLabel}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: isIpoTag
                        ? "rgba(40, 167, 145, 0.14)"
                        : "rgba(242, 153, 74, 0.12)",
                      color: isIpoTag ? "#187e6d" : "#E67E22",
                      border: isIpoTag
                        ? "1px solid rgba(24, 126, 109, 0.32)"
                        : "1px solid rgba(230, 126, 34, 0.35)",
                      height: 22,
                    }}
                  />
                )}
                {isDeletedOrder && (
                  <Chip
                    label="DELETED"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: "rgba(217, 48, 48, 0.12)",
                      color: "#b42318",
                      border: "1px solid rgba(217, 48, 48, 0.3)",
                      height: 22,
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
        <IconButton
          href={`https://www.smest.in/bond-details/${order.security_name.replace(
            /[% ]/g,
            (m) => replaceCharsForSecurityName[m]
          )}/${order.isin}`}
          target="_blank"
          sx={{ ml: 0.5, pt: 0.5 }}
        >
          <LaunchRoundedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: "secondary.main",
          }}
        >
          {order.isin}
        </Typography>
        <IconButton
          sx={{
            p: 0,
            ml: 0.5,
          }}
          onClick={() => copy(order.isin)}
        >
          <ContentCopyRoundedIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          my: 1,
          overflowX: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": {
            height: "5px",
          },
          "&::-webkit-scrollbar-track": {
            marginRight: "150px",
            bgcolor: `${alpha("#7569EE", 0)}`,
            borderRadius: 50,
            border: `2px solid transparent`,
            boxShadow: `inset 0 0 6px ${alpha("#6B6C6C", 0.2)}`,
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              // "linear-gradient(120deg, #c3f74b 0%, #67ce74 100%)",
              "#2e385a",
            borderRadius: 50,
          },
        }}
      >
        <Box
          sx={{
            width: "max-content",
            pr: 4,
            pb: 1,
          }}
        >
          <Grid container spacing={2}>
            {summaryItems.map((item, index) => (
              <Grid key={index} item xs="auto">
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {item.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    opacity: 0.5,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      <Box
        sx={{
          height: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        {onDeleteOrder && canDelete && (
          <Tooltip title={deleteLabel}>
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteOrder(order);
                }}
                disabled={deletingOrderUuid === order.uuid}
                sx={{
                  p: 0,
                  m: 0,
                }}
              >
                {deletingOrderUuid === order.uuid ? (
                  <CircularProgress size={14} />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton
          size="small"
          onClick={() =>
            setOpenMain((prev) => ({
              ...prev,
              [order.uuid]: !prev[order.uuid],
            }))
          }
          sx={{
            p: 0,
            m: 0,
          }}
        >
          {openMain[order.uuid] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={openMain[order.uuid]} timeout="auto" unmountOnExit>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={1} alignItems={"end"}>
                {[
                  {
                    label: "Date (Type)",
                    value: `${
                      displayMoment ? displayMoment.format("MMM DD,YYYY") : "-"
                    } (${displayLabel})`,
                  },
                  {
                    label: "Settlement Date",
                    value: order.settlement_date,
                  },
                  ...(externalTagLabel === "EXTERNAL" && orderSource
                    ? [
                        {
                          label: "Source",
                          value: orderSource,
                        },
                      ]
                    : []),
                ].map((item, index) => (
                  <Grid
                    key={index}
                    item
                    xs={6}
                    sx={{
                      textAlign: index % 2 === 0 ? "left" : "right",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {item.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        opacity: 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      background: "#17c417",
                      position: "relative",
                      height: "100%",
                      width: "100%",
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        color: "#FFFFFF",
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} textAlign={"center"}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Final Payout
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {toINR(principalPayout + interestPayout, 2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                            Principal Payout
                          </Typography>
                          <Typography sx={{ fontWeight: 800 }}>
                            {toINR(principalPayout, 2)}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          textAlign={"center"}
                          fontSize={24}
                          sx={{ fontWeight: 800 }}
                        >
                          +
                        </Grid>
                        <Grid item xs={5} textAlign="right">
                          <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                            Interest Payout
                          </Typography>
                          <Typography sx={{ fontWeight: 800 }}>
                            {toINR(interestPayout, 2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                position: "relative",
                mt: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  position: "absolute",
                  top: -10,
                  left: 10,
                  fontWeight: 600,
                  fontSize: 9,
                  px: 2,
                  py: 0.3,
                  borderRadius: 1,
                  transform: "skew(-13deg)",
                  color: "#d4fc79",
                  background:
                    "linear-gradient(to right, #434343 0%, black 100%)",
                  boxShadow:
                    "-8px 8px 16px rgba(205, 205, 205, 0.2), 8px -8px 16px rgba(205, 205, 205, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.9), 8px 8px 20px rgba(205, 205, 205, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.3), inset -1px -1px 2px rgba(205, 205, 205, 0.5)",
                }}
              >
                Year-on-Year Overview
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "block",
                }}
              >
                <Table
                  size="small"
                  padding="none"
                  sx={{
                    mt: 1,
                    width: "100%",
                    tableLayout: "auto",
                    "& .MuiTableCell-root": {
                      px: 1,
                      py: 0.5,
                      minWidth: "auto",
                      fontSize: "0.5rem",
                    },
                    "& .MuiTypography-root": {
                      fontSize: "0.5rem",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <IconCell>
                        <IconButton
                          size="small"
                          onClick={toggleAllYears}
                          sx={{ p: 0, m: 0 }}
                        >
                          <KeyboardDoubleArrowDownRoundedIcon
                            sx={{
                              color: areAllYearsExpanded
                                ? "secondary.main"
                                : "inherit",
                              transform: areAllYearsExpanded
                                ? "rotate(180deg)"
                                : "none",
                              transition: "transform 0.3s",
                            }}
                          />
                        </IconButton>
                      </IconCell>
                      <TableCell>Year</TableCell>
                      <TableCell>Interest Payment Schedule</TableCell>
                      <TableCell>₹ Interest Payout Post TDS</TableCell>
                      <TableCell>₹ Redemption Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aggregatedCashflows[order.uuid]?.map((yearData) => (
                      <React.Fragment key={`${order.uuid}-${yearData.year}`}>
                        <TableRow
                          hover
                          sx={{
                            "& td": {
                              border: "none !important",
                            },
                          }}
                        >
                          <IconCell>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setOpenYear((prev) => ({
                                  ...prev,
                                  [order.uuid]: {
                                    ...prev[order.uuid],
                                    [yearData.year]:
                                      !prev[order.uuid]?.[yearData.year],
                                  },
                                }))
                              }
                              sx={{ p: 0, m: 0 }}
                            >
                              <KeyboardDoubleArrowDownRoundedIcon
                                sx={{
                                  color: openYear[order.uuid]?.[yearData.year]
                                    ? "secondary.main"
                                    : "inherit",
                                }}
                              />
                            </IconButton>
                          </IconCell>
                          <TableCell>{yearData.year}</TableCell>
                          <TableCell>
                            {yearData.frequency_of_interest}
                          </TableCell>
                          <TableCell>
                            {toINR(yearData.interestAfterTax, 2)}
                          </TableCell>
                          <TableCell>{toINR(yearData.maturity, 2)}</TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            "& td": {
                              border: "none !important",
                            },
                          }}
                        >
                          <TableCell style={{ padding: 0 }} colSpan={5}>
                            <Collapse
                              in={openYear[order.uuid]?.[yearData.year]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  my: 2,
                                  position: "relative",
                                  maxHeight: 200,
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                  display: "block",
                                }}
                              >
                                <Table
                                  size="small"
                                  padding="none"
                                  sx={{
                                    width: "100%",
                                    tableLayout: "auto",
                                    "& .MuiTableCell-root": {
                                      px: 1,
                                      py: 0.5,
                                      minWidth: "auto",
                                      fontSize: "0.5rem",
                                    },
                                    "& .MuiTypography-root": {
                                      fontSize: "0.5rem",
                                    },
                                  }}
                                >
                                  <TableHead>
                                    <TableRow
                                      sx={{
                                        visibility: "collapse",
                                      }}
                                    >
                                      <IconCell>
                                        <KeyboardDoubleArrowDownRoundedIcon />
                                      </IconCell>
                                      <TableCell>Year</TableCell>
                                      <TableCell>
                                        Interest Payment Schedule
                                      </TableCell>
                                      <TableCell>
                                        ₹ Interest Payout Post TDS
                                      </TableCell>
                                      <TableCell>₹ Redemption Amount</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {Object.values(yearData.dateMap).map(
                                      (d) => {
                                        const row = d as {
                                          date: string;
                                          interest: number;
                                          interestAfterTax: number;
                                          maturity: number;
                                        };
                                        return (
                                          <TableRow
                                            hover
                                            key={row.date}
                                            sx={{
                                              "& .MuiTableCell-root": {
                                                color: "success.main",
                                              },
                                            }}
                                          >
                                            <TableCell />
                                            <TableCell>
                                              {yearData.year}
                                            </TableCell>
                                            <TableCell>
                                              {moment(row.date).format(
                                                "MMM DD,YYYY"
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {toINR(row.interestAfterTax, 2)}
                                            </TableCell>
                                            <TableCell>
                                              {toINR(row.maturity, 2)}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
};

export default MobileView;
